import { useTranslation } from "react-i18next";
import { AlertTriangle, PackageSearch, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

export const LoadingGrid = ({ count = 8 }) => (
  <div
    className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    data-testid="loading-skeleton"
  >
    {Array.from({ length: count }).map((_, i) => (
      <Card key={i} className="overflow-hidden p-0">
        <Skeleton className="aspect-[4/3] w-full rounded-none" />
        <div className="space-y-3 p-4">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-9 w-full" />
        </div>
      </Card>
    ))}
  </div>
);

export const LoadingBlock = () => {
  const { t } = useTranslation();
  return (
    <div className="space-y-3" data-testid="loading-skeleton">
      <span className="sr-only">{t("common.loading")}</span>
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
};

export const EmptyState = ({ title, actionLabel, onAction, icon }) => (
  <Card className="flex flex-col items-start gap-4 rounded-2xl border-dashed p-8" data-testid="empty-state">
    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary text-muted-foreground">
      {icon || <PackageSearch className="h-5 w-5" aria-hidden="true" />}
    </span>
    <p className="text-sm text-muted-foreground">{title}</p>
    {actionLabel && onAction && (
      <Button variant="outline" onClick={onAction} data-testid="empty-state-action-button">
        {actionLabel}
      </Button>
    )}
  </Card>
);

export const ErrorState = ({ message, onRetry }) => {
  const { t } = useTranslation();
  return (
    <Card
      className="flex flex-col items-start gap-3 border-destructive/30 bg-destructive/5 p-6"
      data-testid="error-state"
    >
      <div className="flex items-center gap-2 text-destructive">
        <AlertTriangle className="h-5 w-5" aria-hidden="true" />
        <h2 className="text-sm font-semibold">{t("errors.title")}</h2>
      </div>
      <p className="text-sm text-foreground/80">{message || t("errors.generic")}</p>
      {onRetry && (
        <Button variant="outline" onClick={onRetry} data-testid="error-state-retry-button">
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
          {t("errors.retry")}
        </Button>
      )}
    </Card>
  );
};

export const PageHeading = ({ eyebrow, title, subtitle, actions, testId }) => (
  <div
    className="flex flex-col gap-5 border-b pb-8 sm:flex-row sm:items-end sm:justify-between"
    data-testid={testId}
  >
    <div>
      {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
      <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
        {title}
      </h1>
      {subtitle && (
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">{subtitle}</p>
      )}
    </div>
    {actions}
  </div>
);
